import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import {
  BuildingData,
  EditedBuildingData,
} from '../../../../interfaces/building-interfaces';
import { colors } from '../../../../style-utils/colors';
import { Text } from '../../../../style-utils/text';
import { RightArrow } from '../../../icons/right-arrow';
import {
  AddNewPropertyForm,
  AddNewPropertyFormContainer,
  BuildingModalCard,
  ButtonTitle,
  ButtonsContainer,
  CancelButton,
  DashboardContent,
  FieldError,
  FormBanner,
  FormFieldset,
  InputContainer,
  Label,
  ModalScrim,
  ModalScrollColumn,
  ReadOnlyNote,
  SaveButton,
  StyledBuildingInput,
  StyledBuildingSelect,
  Title,
} from './dashboard-buildings.styled';
import { ModalPortal } from '../../../modal/modal-portal';
import {
  InlineMessage,
  Staleable,
  StatusLabel,
  scrimMotion,
  useOverlayMotion,
} from '../../../../motion';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { BuildingPortfolioField } from './building-portfolio-field';
import { BuildingUnitsSection } from './building-units-section';
import { BuildingPassesSection } from './building-passes-section';
import { BuildingStaysSection } from './building-stays-section';
import { BuildingLinkSection } from './building-link-section';
import { staysIsOn, useStaysEntitlement } from '../../../hooks/use-stays-entitlement';
import { EntityGallery } from '../../../gallery/entity-gallery';
import { useAuth } from '../../../../context/auth-provider';
import { deskInPlan } from '../../../../utils/products';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import {
  BuildingLocationPicker,
  BuildingLocation,
  EMPTY_LOCATION,
} from './building-location-picker';
import {
  BuildingTimeZoneField,
  useBuildingZone,
  ZoneSource,
} from './building-time-zone';
import { ZoneChangeAsk, zoneChangeAsk } from './zone-change-ask';
import { regexValidation } from '../../../../function-utils/regex-validation';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiRoute } from '../../../../constants/api-constants';
import {
  HierarchyApiError,
  moveBuildingToPortfolio,
} from '../../../../api/hierarchy-client';
import toast from '../../../../utils/toast';
import {
  focusFirstInvalid,
  focusField,
  serverFieldError,
} from '../../../../utils/field-errors';

const buildingInitialState: BuildingData = {
  id: '',
  property_name: '',
  country: 'USA',
  address: '',
  city: '',
  staffing_code: '',
  email: [],
  phone_number: '',
};

/**
 * Edit one property, as a MODAL over the buildings list.
 *
 * `onClose` / `onSaved` are optional and mirror `dashboard-unit-form.tsx:61-67`.
 * When they are absent the component falls back to navigating to the list, which
 * is what it did as a page — so a caller that has not been converted still
 * behaves, rather than leaving the user in a modal with no exit.
 *
 * `canEdit` false = building:read without building:edit (every property manager,
 * every concierge). The fields render, disabled, and Save is not there at all.
 * The alternative — hiding the row click from those roles — would leave a
 * property manager with no route from the buildings list into the building, and
 * the payload behind this form is the same one the list already holds, so
 * showing it reveals nothing new.
 */
interface DashboardEditBuildingByIdProps {
  editableBuildingData: BuildingData | undefined;
  canEdit?: boolean;
  onClose?: () => void;
  onSaved?: () => void;
  // Raised when a unit under this building was created or saved, because the
  // buildings list behind the modal renders a server-computed units_count.
  // Optional: without it the section still works, the row count just waits for
  // the next natural refresh.
  onUnitsChanged?: () => void;
}

interface StaffingOption {
  code: string;
  label: string;
}

export const DashboardEditBuildingById: React.FC<
  DashboardEditBuildingByIdProps
> = ({
  editableBuildingData,
  canEdit: mayEdit = true,
  onClose,
  onSaved,
  onUnitsChanged,
}) => {
  /* MR-27: A ROW LABELLED "Open <building>" OPENED THE EDIT FORM.
   *
   * The report asked for "a detail view with Edit as a secondary action", and
   * that view already existed here — every read-only rendering below is what a
   * property manager (building:read without building:edit) has always seen:
   * heading "Building details", a disabled fieldset, Close instead of Cancel and
   * no Save. What was missing is that an OWNER never got it. Pressing a
   * building's name put them straight into a live form with ten editable fields
   * and a Save button, which is a destructive-adjacent surface to land on by
   * accident when all you wanted was to look.
   *
   * So the modal now OPENS in that view for everybody, and editing is a thing
   * you ask for. `mayEdit` is the permission; `editing` is the intent; `canEdit`
   * — the name every branch below already reads — is both.
   */
  const [editing, setEditing] = useState(false);
  const canEdit = mayEdit && editing;
  const { permissions, user } = useAuth();
  const canReadUnits = can(permissions, PERMISSIONS.UNIT_READ);
  // The id the ?edit= param named: the caller only ever mounts this with the
  // building it FOUND by matching that param, so this survives a refresh for
  // the same reason the form's own values do. BuildingData.id is a string on
  // these legacy screens (building-interfaces.ts:2) and buildingInitialState
  // seeds it as '', so a non-numeric id means "no building to list units for"
  // rather than a request to /buildings/NaN/units.
  const unitsBuildingId = Number(editableBuildingData?.id);
  const canShowUnits =
    canReadUnits && Number.isInteger(unitsBuildingId) && unitsBuildingId > 0;
  // The desk's reach over visitor passes (migration 118): shown to whoever may
  // read passes, changed by whoever may manage them — see the section itself.
  const canShowPasses =
    can(permissions, PERMISSIONS.VISITORPASS_READ) &&
    Number.isInteger(unitsBuildingId) &&
    unitsBuildingId > 0;
  // Lobbero Stays (plan S1, S-6): the building's consent switch — for stays:read,
  // and only when the organisation has Stays.
  const canReadStays = can(permissions, PERMISSIONS.STAYS_READ);
  const staysOn = staysIsOn(useStaysEntitlement({ enabled: canReadStays }).state);
  // Not in a Stays-only organisation (Independent Stays IS1, R-IS1.6): every
  // building there is always Stays-enabled (migration 173), so there is no
  // consent switch to show.
  const canShowStays =
    canReadStays &&
    staysOn &&
    deskInPlan(user) &&
    Number.isInteger(unitsBuildingId) &&
    unitsBuildingId > 0;

  // Independent Stays IS7: an owner or manager of a Desk account, with the switch on.
  const canShowLinks =
    user?.stays_building_link === true &&
    can(permissions, PERMISSIONS.BUILDING_LINK_HOSTS) &&
    deskInPlan(user) &&
    Number.isInteger(unitsBuildingId) &&
    unitsBuildingId > 0;

  const [updatedBuildingData, setUpdatedBuildingData] = useState<
    BuildingData | EditedBuildingData
  >(
    editableBuildingData && editableBuildingData.property_name.length > 0
      ? editableBuildingData
      : buildingInitialState
  );
  // The building's own clock. Seeded from what is stored and NOT from the
  // browser's zone: this form edits a building that already has an answer, and
  // quietly proposing a different one is how a desk day moves without anyone
  // asking for it. Blank means the column keeps whatever it holds.
  const savedTimezone =
    typeof editableBuildingData?.timezone === 'string'
      ? editableBuildingData.timezone
      : '';
  const zoneState = useBuildingZone({
    savedZone: savedTimezone,
    savedSource: (editableBuildingData as unknown as { timezone_source?: ZoneSource })
      ?.timezone_source,
  });
  // A zone change is confirmed, never slipped through: the server answers the
  // first save with what would change and waits for an explicit yes.
  const [zoneConfirm, setZoneConfirm] = useState<ZoneChangeAsk | null>(null);
  // The picked place, seeded from the building so the map opens on its saved
  // pin. Held apart from updatedBuildingData because the picker writes it as a
  // whole object rather than field-by-field through handleInputChange.
  const [location, setLocation] = useState<BuildingLocation>({
    ...EMPTY_LOCATION,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const submitRef = useRef(false);
  const [staffingOptions, setStaffingOptions] = useState<StaffingOption[]>([]);
  const [invalidFieldsInputArray, setInvalidFieldsInputArray] = useState<
    string[]
  >([]);
  // Typed an address, never picked a match — see the same state in
  // dashboard-add-new-building.tsx. The picker owns the fact because the typed
  // text is its local state and never reaches this component.
  const [locationUnresolved, setLocationUnresolved] = useState(false);
  // The portfolio the building is in today, as the select's string value.
  //
  // PUT /dashboard/buildings/edit-property does NOT accept portfolio_id — it is
  // deliberately allowlisted against mass-assignment — so this choice is kept
  // apart from the form body and applied with its own move once the save lands.
  const seededPortfolioId = editableBuildingData?.portfolio_id
    ? String(editableBuildingData.portfolio_id)
    : '';
  const [portfolioId, setPortfolioId] = useState<string>(seededPortfolioId);
  const [formError, setFormError] = useState('');

  /**
   * HAS THE READER CHANGED ANYTHING?
   *
   * Tracked explicitly rather than by diffing against a snapshot. The obvious
   * implementation — serialise the form when it loads, compare on every render
   * — is wrong on THIS screen: `location` and `timezone` are seeded by effects
   * that run after the building arrives, so a baseline captured when the id
   * lands is a baseline taken before the form finished filling itself in, and
   * every freshly-opened building looked dirty.
   *
   * A ref, not state, because nothing renders differently for it; it is only
   * read at the moment somebody tries to leave.
   */
  const dirtyRef = useRef(false);
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  const navigate = useNavigate();

  // One exit, whether the caller owns the modal or the component is still being
  // mounted the old page way.
  const dismiss = () => {
    if (onClose) {
      onClose();

      return;
    }

    navigate('/dashboard/buildings');
  };

  /**
   * UNSAVED WORK, GUARDED — the worst case in the product.
   *
   * Escape, a scrim mousedown and the × all discarded immediately and
   * unconditionally. This form holds a name, a location picked by hand on a
   * map, a timezone, contact details and a portfolio move; one stray key threw
   * all of it away with no confirmation and no undo. The shared FormModal grew
   * an `isDirty` guard for exactly this, but this modal predates it and rolls
   * its own portal, so it gets the same contract locally.
   *
   * Compared against the values the form LOADED with, not against a blank
   * object: opening a building and closing it again has lost nothing and must
   * not be interrogated.
   */
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const requestDismiss = () => {
    if (dirtyRef.current) {
      setConfirmDiscard(true);

      return;
    }

    dismiss();
  };

  // Escape and the Tab trap come from the shared dialog stack (WP-06), NOT from
  // a keydown listener of our own: a unit form opened from inside this modal has
  // to be able to take Escape without discarding unsaved building edits.
  //
  // `submitRef` is read rather than `isLoading` because the ref is current
  // inside the listener the effect installed on mount, while the state value
  // there is the one captured at that render.
  const dialogRef = useDialogFocus<HTMLDivElement>(true, () => {
    if (!submitRef.current) {
      requestDismiss();
    }
  });
  // The card grows from the centre; a sheet rising from the bottom under `sm`.
  // The caller mounts this inside a Presence, so the same variants play the
  // exit when the modal closes.
  const cardMotion = useOverlayMotion('modal');

  useEffect(() => {
    setPortfolioId(seededPortfolioId);
  }, [seededPortfolioId]);


  // Seed the map from the saved building. Coordinates arrive as numbers (the
  // backend coerces the DECIMAL columns), but guard anyway — a building created
  // before migration 035, or through the signup wizard, simply has none.
  useEffect(() => {
    if (!editableBuildingData) return;

    const b = editableBuildingData as unknown as Record<string, unknown>;
    const num = (raw: unknown): number | null =>
      raw === null || raw === undefined || raw === '' ? null : Number(raw);
    const str = (raw: unknown): string | null =>
      typeof raw === 'string' && raw.length > 0 ? raw : null;

    setLocation({
      latitude: num(b.latitude),
      longitude: num(b.longitude),
      place_id: str(b.place_id),
      place_provider: str(b.place_provider),
      formatted_address: str(b.formatted_address),
      address: str(b.address),
      city: str(b.city),
      region: str(b.region),
      postal_code: str(b.postal_code),
      country: str(b.country),
      country_code: str(b.country_code),
    });
  }, [editableBuildingData]);

  useEffect(() => {
    axios
      .get(`${apiRoute}/staffing-models`)
      .then(response => {
        setStaffingOptions(response.data || []);
      })
      .catch(() => {
        // staffing is optional; the select still renders a prompt
      });
  }, []);

  // properties.email is an array; the form edits one value. Seed it from the
  // first entry — this used to fan the array out into ten named fields and back
  // again, which is the round trip that made the tenth-email machinery exist.
  useEffect(() => {
    if (!editableBuildingData) {
      return;
    }

    const first = Array.isArray(editableBuildingData.email)
      ? editableBuildingData.email[0] ?? ''
      : (editableBuildingData.email as string) ?? '';

    // Cast because the state is a BuildingData | EditedBuildingData union and
    // the two disagree about property_image (string vs File) — spreading either
    // one widens it past both. The value being set here is just the email.
    setUpdatedBuildingData(
      current =>
        ({ ...current, email: first } as BuildingData | EditedBuildingData)
    );
  }, [editableBuildingData]);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const validateNewBuildingData = (data: any) => {
    // Only the name is genuinely required.
    //
    // country / address / city / phone_number were all required here, but every
    // one of them is NULLABLE in the database (migration 024 relaxed the NOT
    // NULLs) and the signup wizard never collects them — it provisions a
    // building from name / units / city / staffing alone. The result was that a
    // building created at signup could NEVER be saved from this form: it opened
    // fine, then refused to submit with "Please enter address*" for a field the
    // user was never asked for. Editing was effectively impossible.
    const requiredFields: string[] = ['property_name'];

    const emptyFields: string[] = [];

    requiredFields.forEach(field => {
      if (!data[field] || data[field].length === 0) {
        emptyFields.push(field);
      }
    });

    // An address typed and not picked is an error; an address never typed is
    // not. Editing a building must not be able to blank its city silently.
    if (locationUnresolved) {
      emptyFields.push('location');
    }

    // One optional property email; only checked when something was typed.
    const typedEmail = updatedBuildingData.email;
    if (typeof typedEmail === 'string' && typedEmail.length > 0) {
      if (!regexValidation('email', typedEmail).isValidEmail) {
        emptyFields.push('email');
      }
    }

    return emptyFields;
  };

  /* Every user-driven write goes through one of the four below, and each one
     marks the form dirty. Server-driven seeding does NOT — which is the whole
     distinction the guard rests on. */
  const handleInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    markDirty();
    const { name, value } = event.target;
    if (name === 'phone_number') {
      // Validate phone number input to allow only numeric characters
      let numericValue = value.replace(/\D/g, '');
      numericValue = '+' + numericValue;

      setUpdatedBuildingData({ ...updatedBuildingData, [name]: numericValue });
    } else {
      setUpdatedBuildingData({ ...updatedBuildingData, [name]: value });
    }
  };

  // Run only after the property edit itself succeeds, and only when the choice
  // actually changed. BuildingData.id is a string on these legacy screens while
  // the move takes a number, and the initial state seeds it as '' — so a
  // building without a usable id is skipped rather than sent to a URL like
  // /api/buildings//portfolio.
  const movePortfolioIfChanged = async () => {
    if (!portfolioId || portfolioId === seededPortfolioId) {
      return;
    }

    const buildingId = Number(updatedBuildingData.id);

    if (!buildingId || Number.isNaN(buildingId)) {
      return;
    }

    try {
      await moveBuildingToPortfolio(buildingId, Number(portfolioId));
    } catch (moveError) {
      // The property was saved — only the move failed, so say only that.
      toast.error(
        moveError instanceof HierarchyApiError
          ? moveError.message
          : 'The building was saved, but it could not be moved to that portfolio.'
      );
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    await save(false);
  };

  // `confirmZoneChange` is true only on the second pass, after the person has
  // read what a new time zone changes and said yes.
  const save = async (confirmZoneChange: boolean) => {
    if (submitRef.current) {
      return;
    }

    const invalidFields = validateNewBuildingData(updatedBuildingData);

    setInvalidFieldsInputArray(invalidFields);

    if (invalidFields.length > 0) {
      /* A SILENT `return` WAS THE WHOLE PROBLEM. The inline labels sit next to
         their fields, inside a fixed-height scrolling card — so on a long form
         the one that objected is frequently off-screen, and pressing Save
         looked like pressing a dead button. The Add form fixed exactly this
         with a banner; this one kept the silence. */
      setFormError(
        invalidFields.length === 1
          ? 'One field needs attention before this can be saved.'
          : `${invalidFields.length} fields need attention before this can be saved.`
      );
      /* AND GO THERE. The banner alone still leaves the reader hunting: this
         card is a fixed-height scroller and the field that objected is usually
         below the fold. Focus moves the screen reader's cursor as well as the
         viewport, which scrollIntoView on its own does not. */
      focusFirstInvalid(invalidFields);

      return;
    }

    setFormError('');

    submitRef.current = true;
    setIsLoading(true);
    try {
      const formData = new FormData();

      formData.append('id', updatedBuildingData.id);
      formData.append('property_name', updatedBuildingData.property_name);
      // The picked place. city / address / country come from here now — the
      // separate inputs are gone, but the columns are still written because the
      // reports and the roster read them.
      //
      // '' rather than null throughout: FormData stringifies whatever it is
      // given, so appending a JS null sends the four characters "null" — a
      // building loaded with country = NULL used to be saved back with the
      // literal string "null" in the column.
      Object.entries(location).forEach(([field, fieldValue]) => {
        formData.append(field, fieldValue === null ? '' : String(fieldValue));
      });
      formData.append(
        'staffing_code',
        (updatedBuildingData.staffing_code as string) ?? ''
      );
      formData.append('phone_number', updatedBuildingData.phone_number ?? '');
      // The zone is sent only when the field was typed into — an explicit
      // choice. Otherwise the server works it out from the pin by itself, and a
      // zone nobody touched is not re-posted as though somebody had chosen it.
      Object.entries(zoneState.fields()).forEach(([field, fieldValue]) => {
        formData.append(field, fieldValue);
      });
      if (confirmZoneChange) {
        formData.append('confirm_zone_change', '1');
      }

      // Always sent, even when empty: the server treats an empty value as
      // "clear the list", which is the only way to remove an address that is
      // already saved. Omitting it would mean "leave it alone".
      formData.append(
        'email',
        typeof updatedBuildingData.email === 'string'
          ? updatedBuildingData.email
          : ''
      );

      await axios.put(
        `${apiRoute}/dashboard/buildings/edit-property`,
        formData
      );

      // Saved: there is nothing unsaved left to warn about.

      dirtyRef.current = false;

      toast.success('Building updated');
      await movePortfolioIfChanged();
      setInvalidFieldsInputArray([]);
      // The list is still mounted behind this modal now, so it does NOT refetch
      // by remounting the way it did when this was a page. onSaved is what
      // refreshes it — without that call the edited row keeps its old values.
      if (onSaved) {
        onSaved();
      } else {
        navigate('/dashboard/buildings');
      }
    } catch (error) {
      /* A NEW TIME ZONE IS ASKED ABOUT, NOT REFUSED. The server answers 409
         with what would change; the dialog below puts it to the person and a
         yes saves again with the confirmation. An open shift is different — it
         has to be closed first, so that one is simply said. */
      const ask = zoneChangeAsk(error);
      if (ask) {
        setZoneConfirm(ask);

        return;
      }
      /* WHEN THE SERVER NAMES A FIELD, USE IT. A rejected time zone comes back
         as { message, field: 'timezone' }; throwing the field away and raising
         a toast put the one useful part of the answer in a corner, detached
         from the input that caused it. Banner + mark + focus instead, and fall
         back to the toast only when there is nothing to point at. */
      const named = serverFieldError(error);
      if (named) {
        setInvalidFieldsInputArray(prev =>
          prev.includes(named.field) ? prev : [...prev, named.field]
        );
        setFormError(named.message);
        /* AFTER THE FORM IS LIVE AGAIN. The fields sit in a fieldset that is
           disabled for the whole of the request, and a disabled input refuses
           focus — so the move has to wait for the render that re-enables them,
           which is why this is a task and not a call. */
        setTimeout(() => focusField(named.field), 0);
      } else {
        const message = axios.isAxiosError<{ message?: string }>(error)
          ? error.response?.data?.message
          : undefined;

        toast.error(
          message || 'Could not update the building. Please try again.'
        );
      }
    } finally {
      submitRef.current = false;
      setIsLoading(false);
    }
  };

  const clearData = () => {
    setUpdatedBuildingData(buildingInitialState);
    setInvalidFieldsInputArray([]);
    dismiss();
  };

  // Nothing usable was handed in. As a page this was the whole story of the
  // refresh bounce — a deep link arrived with no building in React state, the
  // form fell back to buildingInitialState, and this effect threw the user back
  // at the list. The modal caller only mounts this with a building it found, so
  // the guard is now a last resort rather than the normal path.
  useEffect(() => {
    if (updatedBuildingData.property_name === '') {
      dismiss();
    }
  }, [editableBuildingData]);

  return (
    <ModalPortal>
      {/* The scrim fades; the DIALOG is the card. role and aria-modal sit on
          the element that holds the focus trap, not on the backdrop. */}
      <ModalScrim
        {...scrimMotion}
        onMouseDown={event => {
          if (event.target === event.currentTarget && !submitRef.current) {
            requestDismiss();
          }
        }}
      >
        <BuildingModalCard
          {...cardMotion}
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={canEdit ? 'Edit building' : 'Building details'}
          onMouseDown={event => event.stopPropagation()}
        >
          {/* isAddNewBuilding keeps the surface background and, more to the
              point, `overflow: hidden` — the card has an explicit height, so
              this column is what pins ButtonsContainer to the bottom and lets
              AddNewPropertyFormContainer be the single scroller. */}
          <DashboardContent
            id="dashboard-edit-building-form"
            isAddNewBuilding={true}
          >
            <Title>
              <Text
                mt={16}
                ml={16}
                mb={0}
                fontSize={24}
                fontWeight={700}
                color={colors.grey_dark}
              >
                {canEdit ? 'Edit building' : 'Building details'}
              </Text>
              <Text
                mt={8}
                ml={16}
                mb={0}
                fontSize={14}
                color={`${colors.grey}`}
              >
                {canEdit
                  ? 'Please update the building'
                  : editableBuildingData?.property_name || 'This property'}
              </Text>
            </Title>

            <InlineMessage
              role="alert"
              message={
                formError ? <FormBanner as="div">{formError}</FormBanner> : undefined
              }
            />


            <AddNewPropertyFormContainer>
              {/* One column, one scroller. The units section is a SIBLING of
                  the form rather than a child of it — a <button> inside a
                  <form> defaults to type="submit", and the section is not part
                  of the building's payload — but it stays INSIDE the scroller,
                  because a sibling of AddNewPropertyFormContainer would give
                  this fixed-height card a second scroll region. */}
              <ModalScrollColumn>
                {/* SAVING KEEPS THE FORM ON SCREEN. The absolute spinner that
                    used to cover these fields is gone: while the PUT is in
                    flight the form stays readable, dims (Staleable), is inert
                    (the fieldset is disabled, aria-busy says so) and the Save
                    button itself carries the pending state — its width never
                    changes, because StatusLabel stacks both labels in one
                    cell. */}
                <AddNewPropertyForm
                  id="dashboard-edit-building-fields"
                  aria-busy={isLoading || undefined}
                  onSubmit={onSubmit}
                >
                  {!canEdit && (
                    <ReadOnlyNote>
                      You can see this property but not change it. Property
                      details are edited by the account owner.
                    </ReadOnlyNote>
                  )}

                  {/* One `disabled` for the whole subtree. The location picker and
                      the portfolio field take no disabled prop of their own, and a
                      fieldset is the only thing in HTML that reaches every control
                      inside it. Always rendered, so the layout is identical
                      whether or not the user may edit. */}
                  <Staleable stale={isLoading}>
                  <FormFieldset disabled={!canEdit || isLoading}>
                    {/* The old single "Upload photo" control stood here. It is
                        gone: the server ignores `property_image`, so it took a
                        file and silently dropped it. Building photos are the
                        gallery further down this modal (EntityGallery). */}

                    <InputContainer>
                      <Label htmlFor="edit-building-name">Building name</Label>
                      <StyledBuildingInput
                        id="edit-building-name"
                        type="text"
                        name="property_name"
                        value={updatedBuildingData.property_name}
                        placeholder={'Enter building name'}
                        onChange={handleInputChange}
                      />
                    </InputContainer>
                    <FieldError
                      show={invalidFieldsInputArray.includes('property_name')}
                    >
                      Please enter building name*
                    </FieldError>

                    {/* Replaces the separate City / Country / Address inputs:
                        picking a place on the map fills all three behind the
                        scenes. */}
                    <BuildingLocationPicker
                      value={location}
                      onChange={next => {
                        markDirty();
                        setLocation(next);
                      }}
                      onUnresolvedChange={setLocationUnresolved}
                      onZoneHint={zoneState.onZoneHint}
                      disabled={!canEdit || isLoading}
                      invalid={invalidFieldsInputArray.includes('location')}
                    />

                    {/* The building's time zone. It follows the map pin; a
                        change — from a moved pin or typed here — is confirmed
                        before it is saved, because every day-grouped figure
                        for the building is re-cut around the new clock. */}
                    <BuildingTimeZoneField
                      idPrefix="edit-building"
                      state={zoneState}
                      onDirty={markDirty}
                      readOnly={!canEdit}
                    />

                    <InputContainer>
                      <Label htmlFor="edit-building-staffing">
                        Front desk staffing
                      </Label>
                      <StyledBuildingSelect
                        id="edit-building-staffing"
                        name="staffing_code"
                        value={
                          (updatedBuildingData.staffing_code as string) ?? ''
                        }
                        onChange={handleInputChange}
                      >
                        <option value="">Select…</option>
                        {staffingOptions.map(option => (
                          <option key={option.code} value={option.code}>
                            {option.label}
                          </option>
                        ))}
                      </StyledBuildingSelect>
                    </InputContainer>

                    <BuildingPortfolioField
                      mode="edit"
                      portfolioId={portfolioId}
                      newPortfolioName=""
                      onChange={next => setPortfolioId(next.portfolioId)}
                    />

                    <InputContainer>
                      <Label htmlFor="edit-building-email">
                        Property email (optional)
                      </Label>
                      <StyledBuildingInput
                        id="edit-building-email"
                        type="email"
                        name="email"
                        value={(updatedBuildingData.email as string) ?? ''}
                        placeholder={'Enter building email address'}
                        onChange={handleInputChange}
                      />
                    </InputContainer>
                    <FieldError show={invalidFieldsInputArray.includes('email')}>
                      Please enter a valid email address*
                    </FieldError>

                    {/* One plain phone box, showing exactly what is stored. The
                        country-code <select> that stood beside it is gone, and
                        with it this form's ability to destroy a saved number by
                        being touched: its handler blanked phone_number BEFORE
                        writing the prefix, it was hardcoded to '+1' and was never
                        seeded from the number already saved, and properties.js
                        turns an empty string into NULL. A stray click or a
                        mouse-wheel over that native select therefore wiped a
                        saved +44 number and replaced it with a US one. The wire
                        field was always this single string. */}
                    <InputContainer>
                      <Label htmlFor="edit-building-phone">
                        Phone (optional)
                      </Label>
                      <StyledBuildingInput
                        id="edit-building-phone"
                        type="tel"
                        name="phone_number"
                        autoComplete="tel"
                        value={updatedBuildingData.phone_number ?? ''}
                        placeholder={'Enter phone number'}
                        onChange={handleInputChange}
                      />
                    </InputContainer>
                    <FieldError
                      show={invalidFieldsInputArray.includes('phone_number')}
                    >
                      Please enter a valid phone number*
                    </FieldError>
                  </FormFieldset>
                  </Staleable>
                </AddNewPropertyForm>

                {/* Item 3 — this building's units, edited IN PLACE. Gated on
                    unit:read, the same permission the server puts on
                    GET /buildings/:id/units, so a role that would only get a
                    403 is not shown an empty table instead.

                    Mounted CONDITIONALLY rather than returning null from
                    inside: the section owns hooks (its own fetch and the
                    allowance read), and a caller-side gate is what keeps them
                    from running — and from firing a request — for someone who
                    may not read units at all. */}
                {canShowUnits && (
                  <BuildingUnitsSection
                    buildingId={unitsBuildingId}
                    buildingName={
                      editableBuildingData?.property_name || 'this building'
                    }
                    onUnitsChanged={onUnitsChanged}
                  />
                )}

                {/* What the front desk may do with visitor passes here. Saves
                    on its own route; the list behind the modal re-reads so the
                    next opening shows what was saved. */}
                {canShowPasses && (
                  <BuildingPassesSection
                    buildingId={unitsBuildingId}
                    buildingName={
                      editableBuildingData?.property_name || 'this building'
                    }
                    access={editableBuildingData?.concierge_pass_access}
                    onChanged={() => onUnitsChanged?.()}
                  />
                )}

                {/* Lobbero Stays: the building's consent to short-term
                    listings. Saves on its own route, like the section above. */}
                {canShowStays && (
                  <BuildingStaysSection
                    buildingId={unitsBuildingId}
                    buildingName={
                      editableBuildingData?.property_name || 'this building'
                    }
                    enabled={editableBuildingData?.stays_enabled === true}
                    onChanged={() => onUnitsChanged?.()}
                  />
                )}

                {/* Independent Stays IS7: short-stay hosts of OTHER accounts
                    linked to this building's front desk. */}
                {canShowLinks && (
                  <BuildingLinkSection
                    buildingId={unitsBuildingId}
                    buildingName={
                      editableBuildingData?.property_name || 'this building'
                    }
                  />
                )}

                {/* Item 4 — the building's photos. Mounted CONDITIONALLY for
                    the same reason as the units section above: the gallery owns
                    a fetch, and a building with no numeric id yet (the param
                    can be a non-numeric legacy value) has nothing to list.

                    Read-only for building:read — the grid and the full-size
                    viewer still work, only the add/reorder/cover/delete
                    controls are withheld. */}
                {Number.isInteger(unitsBuildingId) && unitsBuildingId > 0 && (
                  <EntityGallery
                    type="building"
                    entityId={unitsBuildingId}
                    canEdit={canEdit}
                    title="Building photos"
                  />
                )}
              </ModalScrollColumn>
            </AddNewPropertyFormContainer>
            <ButtonsContainer>
              <CancelButton type="button" onClick={() => clearData()}>
                <ButtonTitle>{canEdit ? 'Cancel' : 'Close'}</ButtonTitle>
              </CancelButton>
              {/* THE SECONDARY ACTION MR-27 ASKED FOR. Only for somebody who
                  may actually edit, and only while they are still reading —
                  once they are in the form, Save is the primary action and this
                  would be a second way to do nothing. */}
              {mayEdit && !editing && (
                <SaveButton
                  type="button"
                  data-testid="building-edit"
                  onClick={() => setEditing(true)}
                >
                  <ButtonTitle>Edit</ButtonTitle>
                  <RightArrow />
                </SaveButton>
              )}
              {/* Absent, not disabled, without building:edit — a greyed Save
                  reads as a broken button. The PUT would 403 anyway; the form
                  simply offers no way to send it.

                  It stays OUTSIDE the form and submits it by document-scoped
                  id, which works inside the portal but forbids two of these
                  mounted at once. The caller keys the modal on the building id
                  and only ever mounts one. */}
              {canEdit && (
                <SaveButton
                  type="submit"
                  form="dashboard-edit-building-fields"
                  disabled={isLoading}
                >
                  <StatusLabel
                    state={isLoading ? 'busy' : 'idle'}
                    labels={{
                      idle: (
                        <>
                          <ButtonTitle>Save</ButtonTitle>
                          <RightArrow />
                        </>
                      ),
                      busy: <ButtonTitle>Saving…</ButtonTitle>,
                    }}
                  />
                </SaveButton>
              )}
            </ButtonsContainer>
          </DashboardContent>
        </BuildingModalCard>
      </ModalScrim>
      <ConfirmDialog
        open={confirmDiscard}
        title="Discard your changes?"
        message="You have unsaved changes to this building. Close anyway and lose them?"
        primaryLabel="Discard changes"
        primaryVariant="destructive"
        dismissLabel="Keep editing"
        onPrimary={() => {
          setConfirmDiscard(false);
          dismiss();
        }}
        onDismiss={() => setConfirmDiscard(false)}
      />
      <ConfirmDialog
        open={zoneConfirm !== null}
        title="Change this building’s time zone?"
        message={zoneConfirm ? zoneConfirm.message : ''}
        primaryLabel="Change time zone"
        dismissLabel="Keep the current one"
        onPrimary={() => {
          setZoneConfirm(null);
          save(true);
        }}
        onDismiss={() => {
          // "Keep the current one" has to MEAN it. Closing the dialog alone
          // left the moved pin in place, so the next Save asked the same
          // question again, for ever. Keeping the zone is an explicit choice of
          // the stored one: the new pin is saved, the clock does not move.
          if (zoneConfirm && zoneConfirm.from) zoneState.choose(zoneConfirm.from);
          setZoneConfirm(null);
        }}
      />
    </ModalPortal>
  );
};
